import { useState, useEffect } from 'react';
import { Card, Button, Badge } from '@atoms/index';
import { PromotionFormModal, AlertModal } from '@organisms/index';
import { promotionService, type Promotion, type CreatePromotionDto } from '@services/promotionService';
import { formatCurrency } from '@/utils/currency';
import { cn } from '@/utils/cn';

const PromotionsTab = () => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState<Promotion | null>(null);
  const [alertState, setAlertState] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });

  useEffect(() => {
    loadPromotions();
  }, []);

  const loadPromotions = async () => {
    setIsLoading(true);
    try {
      const data = await promotionService.getPromotions();
      setPromotions(data);
    } catch (error) {
      console.error('Failed to load promotions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedPromotion(null);
    setIsModalOpen(true);
  };

  const handleEdit = (promotion: Promotion) => {
    setSelectedPromotion(promotion);
    setIsModalOpen(true);
  };

  const handleDelete = async (promotionId: string) => {
    if (!confirm('Bu kampanyayı silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await promotionService.deletePromotion(promotionId);
      await loadPromotions();
    } catch (error) {
      console.error('Failed to delete promotion:', error);
      setAlertState({ isOpen: true, title: 'Hata', message: 'Kampanya silinirken bir hata oluştu' });
    }
  };

  const handleSave = async (dto: CreatePromotionDto) => {
    try {
      if (selectedPromotion) {
        await promotionService.updatePromotion(selectedPromotion.id, dto);
      } else {
        await promotionService.createPromotion(dto);
      }
      await loadPromotions();
      setIsModalOpen(false);
      setSelectedPromotion(null);
    } catch (error) {
      console.error('Failed to save promotion:', error);
      throw error;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'DISCOUNT_PERCENTAGE':
        return 'Yüzde İndirim';
      case 'DISCOUNT_AMOUNT':
        return 'Tutar İndirimi';
      case 'FREE_DELIVERY':
        return 'Ücretsiz Teslimat';
      default:
        return type;
    }
  };

  const formatPromotionValue = (promotion: Promotion) => {
    if (promotion.type === 'FREE_DELIVERY') {
      return 'Ücretsiz';
    }
    if (promotion.type === 'DISCOUNT_PERCENTAGE') {
      return `%${promotion.discountValue}`;
    }
    return formatCurrency(Number(promotion.discountValue) || 0);
  };

  const isActive = (promotion: Promotion) => {
    const now = new Date();
    const start = new Date(promotion.startDate);
    const end = new Date(promotion.endDate);
    return promotion.isActive && now >= start && now <= end;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Kampanyalar</h2>
          <p className="text-gray-600">Kampanyalarınızı yönetin ve yeni kampanyalar oluşturun</p>
        </div>
        <Button variant="primary" onClick={handleCreate}>
          + Yeni Kampanya
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600">Kampanyalar yükleniyor...</p>
        </div>
      ) : promotions.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">Henüz kampanya yok</p>
            <Button variant="primary" onClick={handleCreate}>
              İlk Kampanyanızı Oluşturun
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {promotions.map((promotion) => (
            <Card key={promotion.id} className="hover:shadow-md transition-shadow">
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 mb-1">{promotion.name}</h3>
                    {promotion.description && (
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {promotion.description}
                      </p>
                    )}
                  </div>
                  <Badge variant={isActive(promotion) ? 'success' : 'secondary'}>
                    {isActive(promotion) ? 'Aktif' : 'Pasif'}
                  </Badge>
                </div>

                <div className="flex items-center gap-2">
                  <Badge variant="info">{getTypeLabel(promotion.type)}</Badge>
                  <span className="font-bold text-primary">
                    {formatPromotionValue(promotion)}
                  </span>
                </div>

                {promotion.minimumOrderAmount != null && Number(promotion.minimumOrderAmount) > 0 && (
                  <p className="text-xs text-gray-500">
                    Min. sipariş: {formatCurrency(Number(promotion.minimumOrderAmount))}
                  </p>
                )}

                <div className="text-xs text-gray-500 pt-2 border-t">
                  <p>
                    Başlangıç:{' '}
                    {new Date(promotion.startDate).toLocaleDateString('tr-TR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                  </p>
                  <p>
                    Bitiş:{' '}
                    {new Date(promotion.endDate).toLocaleDateString('tr-TR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                  </p>
                </div>

                <div className="flex gap-2 pt-3 border-t">
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => handleEdit(promotion)}
                    className="flex-1"
                  >
                    Düzenle
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => handleDelete(promotion.id)}
                  >
                    Sil
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {isModalOpen && (
        <PromotionFormModal
          isOpen={isModalOpen}
          promotion={selectedPromotion}
          onSave={handleSave}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedPromotion(null);
          }}
        />
      )}

      <AlertModal
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        variant="danger"
        onClose={() => setAlertState((a) => ({ ...a, isOpen: false }))}
      />
    </div>
  );
};

export default PromotionsTab;
